/**
 * RejectReasonModal — force the recruiter to pick a granular reject
 * reason from the backend catalog (iter97, CEO 2026-09-02).
 *
 * Before iter97, the reject flow submitted `criteres_non_remplis` as a
 * silent default → the /refuses list ended up filled with candidates
 * that all read "Critères non remplis", useless for post-mortem.
 * Now the modal is REQUIRED before every "Refus définitif" action.
 *
 * Props:
 *   - open, onOpenChange
 *   - candidate {first_name, last_name} for the header context
 *   - defaultReason: optional pre-selection (e.g. "numero_injoignable"
 *     when arriving from the CallActionModal)
 *   - onConfirm(reason, note) — called after user picks + confirms.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, ThumbsDown, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { api } from "../../lib/api";


export default function RejectReasonModal({ open, onOpenChange, candidate, defaultReason, onConfirm }) {
    const [reason, setReason] = useState(defaultReason || "");
    const [note, setNote] = useState("");
    const [catalog, setCatalog] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setReason(defaultReason || "");
        setNote("");
        api.get("/candidates/reject-reasons")
            .then(({ data }) => setCatalog(data?.reasons || []))
            .catch(() => setCatalog([]));
    }, [open, defaultReason]);

    // Group reasons by their `group` field (Contact / Critères / Profil / Autre)
    const grouped = useMemo(() => {
        const g = {};
        for (const r of catalog) {
            const key = r.group || "Autre";
            (g[key] = g[key] || []).push(r);
        }
        return g;
    }, [catalog]);

    const canConfirm = Boolean(reason) && !busy;

    const submit = async () => {
        if (!canConfirm) return;
        setBusy(true);
        try {
            await onConfirm?.(reason, note.trim() || null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-[100] bg-shariti-black/60 backdrop-blur-sm" />
                <Dialog.Content
                    data-testid="reject-reason-modal"
                    className="fixed left-1/2 top-1/2 z-[101] w-[92vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-white shadow-2xl focus:outline-none max-h-[92vh] overflow-hidden flex flex-col"
                >
                    <div className="p-5 pb-3 border-b border-slate-100">
                        <div className="flex items-center justify-between mb-2">
                            <Dialog.Title className="text-base font-extrabold text-shariti-black inline-flex items-center gap-2">
                                <ThumbsDown className="h-4 w-4 text-red-500" />
                                Pourquoi refuser {candidate?.first_name} {candidate?.last_name} ?
                            </Dialog.Title>
                            <Dialog.Close asChild>
                                <button type="button" className="h-8 w-8 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500">
                                    <X className="h-4 w-4" />
                                </button>
                            </Dialog.Close>
                        </div>
                        <p className="text-[11px] font-semibold text-slate-500">
                            Le motif apparaîtra en gros à côté du nom dans la liste des refusés
                            — pas de « Critères non remplis » anonyme.
                        </p>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {Object.entries(grouped).map(([groupName, reasons]) => (
                            <div key={groupName}>
                                <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
                                    {groupName}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                    {reasons.map((r) => (
                                        <button
                                            key={r.value}
                                            type="button"
                                            onClick={() => setReason(r.value)}
                                            data-testid={`reject-reason-${r.value}`}
                                            className={`text-left px-3 py-2 rounded-xl border-2 transition text-xs font-bold ${
                                                reason === r.value
                                                    ? "border-red-400 bg-red-50 text-red-800"
                                                    : "border-slate-200 hover:border-red-300 bg-white text-slate-700"
                                            }`}
                                        >
                                            {r.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}

                        <div className="pt-2">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">
                                Note libre (optionnelle)
                            </label>
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows={2}
                                data-testid="reject-reason-note"
                                placeholder="Détails additionnels — ex : « préfère un CDI, refuse le mi-temps »"
                                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-semibold focus:border-red-400 focus:outline-none resize-none"
                            />
                        </div>
                    </div>

                    <div className="p-4 border-t border-slate-100 flex gap-2">
                        <button type="button" onClick={() => onOpenChange(false)}
                            className="flex-1 h-11 rounded-xl border-2 border-slate-200 hover:bg-slate-50 font-black text-sm">
                            Annuler
                        </button>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={!canConfirm}
                            data-testid="reject-reason-submit"
                            className="flex-1 h-11 rounded-xl bg-red-500 hover:bg-red-600 text-white font-black text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ThumbsDown className="h-4 w-4" />}
                            {busy ? "Refus en cours…" : "Refuser définitivement"}
                        </button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
